import React, { useEffect, useState } from 'react';
import './tariffSettings.css';
import { ITariff, ITariffCreateRequest, ITariffUpdateRequest } from '../../types/type';
import { TariffService } from '../../services/tariff.service';

interface TariffSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const TariffSettings: React.FC<TariffSettingsProps> = ({ isOpen, onClose }) => {
  const [tariffs, setTariffs] = useState<ITariff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTariff, setEditingTariff] = useState<ITariff | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [newTariff, setNewTariff] = useState<ITariffCreateRequest>({
    title: '',
    allowPrice: 0,
    countOfRL: 1,
  });

  useEffect(() => {
    if (isOpen) {
      fetchTariffs();
    }
  }, [isOpen]);

  const fetchTariffs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TariffService.getTariffs();
      setTariffs(response.data);
    } catch (error: any) {
      setError(error.message || 'Не удалось загрузить тарифы');
      console.error('Error fetching tariffs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTariff = async () => {
    if (!newTariff.title.trim()) {
      setError('Название тарифа обязательно для заполнения');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const createdTariff = await TariffService.createTariff(newTariff);
      setTariffs([...tariffs, createdTariff]);
      setNewTariff({ title: '', allowPrice: 0, countOfRL: 1 });
      setIsCreating(false);
    } catch (error: any) {
      setError(error.message || 'Не удалось создать тариф');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTariff = async () => {
    if (!editingTariff) return;

    try {
      setSaving(true);
      setError(null);
      const updatedTariff = await TariffService.updateTariff(editingTariff.id, {
        title: editingTariff.title,
        allowPrice: editingTariff.allowPrice,
        countOfRL: editingTariff.countOfRL,
      });
      setTariffs(tariffs.map((t) => (t.id === updatedTariff.id ? updatedTariff : t)));
      setEditingTariff(null);
    } catch (error: any) {
      setError(error.message || 'Не удалось обновить тариф');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTariff = async (id: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот тариф?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await TariffService.deleteTariff(id);
      setTariffs(tariffs.filter((t) => t.id !== id));
    } catch (error: any) {
      setError(error.message || 'Не удалось удалить тариф');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTariff = (tariff: ITariff) => {
    setEditingTariff({ ...tariff });
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    setEditingTariff(null);
    setIsCreating(false);
    setNewTariff({ title: '', allowPrice: 0, countOfRL: 1 });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-tariff">
      <div className="modal-content-tariff tariff-settings">
        <div className="modal-header">
          <h2>Управление тарифами путевых листов</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {loading && <div className="loading-overlay">Загрузка данных...</div>}

          {error && <div className="error-message">{error}</div>}

          {/* Кнопка добавления нового тарифа */}
          {!isCreating && !editingTariff && (
            <div className="add-tariff-section">
              <button className="button add" onClick={() => setIsCreating(true)} disabled={loading || saving}>
                Добавить новый тариф
              </button>
            </div>
          )}

          {/* Форма создания нового тарифа */}
          {isCreating && (
            <div className="tariff-form">
              <h3>Создание нового тарифа</h3>
              <div className="form-row">
                <label>Название:</label>
                <input
                  type="text"
                  value={newTariff.title}
                  onChange={(e) => setNewTariff({ ...newTariff, title: e.target.value })}
                  placeholder="Введите название тарифа"
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Допустимая стоимость:</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newTariff.allowPrice}
                  onChange={(e) => setNewTariff({ ...newTariff, allowPrice: parseFloat(e.target.value) || 0 })}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Количество путевых листов:</label>
                <input
                  type="number"
                  min="1"
                  value={newTariff.countOfRL}
                  onChange={(e) => setNewTariff({ ...newTariff, countOfRL: parseInt(e.target.value) || 1 })}
                  disabled={saving}
                />
              </div>
              <div className="form-actions">
                <button className="button cancel" onClick={handleCancelEdit} disabled={saving}>
                  Отменить
                </button>
                <button className="button save" onClick={handleCreateTariff} disabled={saving}>
                  {saving ? 'Сохранение...' : 'Создать'}
                </button>
              </div>
            </div>
          )}

          {/* Форма редактирования тарифа */}
          {editingTariff && (
            <div className="tariff-form">
              <h3>Редактирование тарифа</h3>
              <div className="form-row">
                <label>Название:</label>
                <input
                  type="text"
                  value={editingTariff.title}
                  onChange={(e) => setEditingTariff({ ...editingTariff, title: e.target.value })}
                  placeholder="Введите название тарифа"
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Допустимая стоимость:</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingTariff.allowPrice}
                  onChange={(e) => setEditingTariff({ ...editingTariff, allowPrice: parseFloat(e.target.value) || 0 })}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Количество путевых листов:</label>
                <input
                  type="number"
                  min="1"
                  value={editingTariff.countOfRL}
                  onChange={(e) => setEditingTariff({ ...editingTariff, countOfRL: parseInt(e.target.value) || 1 })}
                  disabled={saving}
                />
              </div>
              <div className="form-actions">
                <button className="button cancel" onClick={handleCancelEdit} disabled={saving}>
                  Отменить
                </button>
                <button className="button save" onClick={handleUpdateTariff} disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          )}

          {/* Список существующих тарифов */}
          {!isCreating && !editingTariff && (
            <div className="tariffs-list">
              <h3>Существующие тарифы</h3>
              {tariffs.length === 0 && !loading ? (
                <div className="no-tariffs">Тарифы не найдены</div>
              ) : (
                <div className="tariffs-grid">
                  {tariffs.map((tariff) => (
                    <div key={tariff.id} className="tariff-card">
                      <div className="tariff-info">
                        <h4>{tariff.title}</h4>
                        <p>Допустимая стоимость: {tariff.allowPrice} ₽</p>
                        <p>Количество ПЛ: {tariff.countOfRL}</p>
                        <p>Стоимость за один: {tariff.priceForOne} ₽</p>
                      </div>
                      <div className="tariff-actions">
                        <button className="button edit" onClick={() => handleEditTariff(tariff)} disabled={saving}>
                          Редактировать
                        </button>
                        <button
                          className="button delete"
                          onClick={() => handleDeleteTariff(tariff.id)}
                          disabled={saving}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="button cancel" onClick={onClose} disabled={saving}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default TariffSettings;
